// React bindings for the DevRobot motion and theme helpers (DevRobot UI kit).

import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { reducedMotion, transition } from './motion';
import { isDesktopPwa } from './native';
import { currentTheme, readChoice, type ThemeChoice, type ThemeName } from './theme';

/**
 * Whether this is an installed app in a desktop window (see `isDesktopPwa`). It is watched
 * rather than read once: the same document keeps running when someone installs the app from
 * the browser's own menu, and Chromium then reports the new display mode without a reload.
 */
export function useDesktopPwa(): boolean {
  const [appWindow, setAppWindow] = useState(isDesktopPwa);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(display-mode: standalone)');
    const sync = () => setAppWindow(isDesktopPwa());
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return appWindow;
}

/** Ease a number's text into place without re-rendering React on every frame. */
export function useCountUp<T extends HTMLElement = HTMLSpanElement>(value: number, format: (n: number) => string = (n) => Math.round(n).toLocaleString(), { duration = 1100 } = {}) {
  const ref = useRef<T>(null);
  const shown = useRef(value * 0.82);
  const fmt = useRef(format);
  fmt.current = format;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const from = shown.current;
    if (reducedMotion() || from === value || typeof requestAnimationFrame !== 'function') {
      el.textContent = fmt.current(value);
      shown.current = value;
      return;
    }
    const t0 = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / duration);
      shown.current = from + (value - from) * (1 - (1 - t) ** 4);
      el.textContent = fmt.current(t === 1 ? value : shown.current);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      el.textContent = fmt.current(value);
      shown.current = value;
    };
  }, [value, duration]);
  return ref;
}

/** The active DevRobot theme name, kept in sync with toggles and the OS setting. */
export function useThemeName(): ThemeName {
  const [theme, setTheme] = useState(currentTheme);
  useEffect(() => {
    const onChange = (e: Event) => setTheme((e as CustomEvent<ThemeName>).detail);
    document.addEventListener('dr:theme', onChange);
    return () => document.removeEventListener('dr:theme', onChange);
  }, []);
  return theme;
}

/** The stored choice: a pinned theme or "system". */
export function useThemeChoice(): ThemeChoice {
  const [choice, setChoice] = useState(readChoice);
  useEffect(() => {
    const onChange = (e: Event) => setChoice((e as CustomEvent<ThemeChoice>).detail);
    document.addEventListener('dr:theme-choice', onChange);
    return () => document.removeEventListener('dr:theme-choice', onChange);
  }, []);
  return choice;
}

/** Commit a state update inside a list View Transition: items glide instead of jumping. */
export const listTransition = (update: () => void) => transition(() => flushSync(update), { kind: 'list' });

/** Commit a state update inside a plain cross-fading View Transition. */
export const softTransition = (update: () => void) => transition(() => flushSync(update));
